<i18n locale="en">
  {
    "close": "Close"
  }
</i18n>

<template>
  <div
    :class="[
      'dialog-heading flex-row gap-6 align-center justify-between',
      { 'dialog-heading--no-border': hideBorder },
    ]"
  >
    <div class="flex-row gap-3 align-center">
      <slot name="icon-left" />
      <div class="flex-col gap-1">
        <OwnType
          v-if="title"
          :text="title"
          :variant="useNewHeaderStyles ? 'heading-regular' : 'title-large'"
        />
        <OwnType
          v-if="subtitle"
          :text="subtitle"
          :variant="useNewHeaderStyles ? 'paragraph-regular' : 'title-small'"
          :color="useNewHeaderStyles ? 'secondary' : undefined"
        />
      </div>
    </div>
    <OwnButton
      v-if="!isPersistent && !props.hideCloseButton"
      :aria-label="t('close')"
      @click="handleCancel"
    >
      <template #icon-left>
        <PhX size="24" />
      </template>
    </OwnButton>
  </div>
</template>

<script lang="ts" setup>
import { PhX } from '@phosphor-icons/vue'
import { inject, ref, computed, useAttrs, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { OwnButton } from '../OwnButton'
import { OwnType } from '../OwnType'

const { t } = useI18n()
const attrs = useAttrs()

const props = withDefaults(
  defineProps<{
    hideBorder?: boolean
    hideCloseButton?: boolean
    subtitle?: string
    title?: string
    useNewHeaderStyles?: boolean
  }>(),
  {
    hideBorder: false,
    hideCloseButton: false,
    subtitle: undefined,
    title: undefined,
    useNewHeaderStyles: false,
  }
)

// Inject the persistent state from parent dialog
const dialogPersistentRef = inject<Ref<boolean>>('dialogPersistent', ref(false))
const isPersistent = computed(() => dialogPersistentRef.value)

/**
 * Inject the cancel handler from parent OwnDialog.
 * This provides automatic close functionality without requiring explicit @cancel listeners.
 */
const injectedCancel = inject<(() => void) | undefined>(
  'dialogCancel',
  undefined
)

const emit = defineEmits<(event: 'cancel') => void>()

// Check if there's an explicit @cancel listener
const hasExplicitCancelListener = computed(() => 'onCancel' in attrs)

/**
 * Handle the close button click with smart fallback behavior:
 * 1. If explicit @cancel listener exists, use it (allows override)
 * 2. Otherwise, use injected handler from parent OwnDialog (automatic)
 * 3. Fallback to emit for backwards compatibility
 */
const handleCancel = () => {
  if (hasExplicitCancelListener.value) {
    // If there's an explicit listener, emit the event
    emit('cancel')
  } else if (injectedCancel) {
    // Otherwise, use the injected handler from parent dialog
    injectedCancel()
  } else {
    // Fallback: emit anyway (backwards compatibility)
    emit('cancel')
  }
}
</script>

<style lang="scss" scoped>
.dialog-heading {
  border-bottom: 0.5px solid var(--divider);
  padding: 24px;

  &--no-border {
    border-bottom: none;
  }
}
</style>
